import React, { type CSSProperties } from 'react'

export type ImportStepStatus = 'pending' | 'running' | 'done' | 'failed'

export interface ImportStep {
  label: string
  status: ImportStepStatus
  error?: string
}

interface ImportProgressModalProps {
  open: boolean
  title: string
  steps: ImportStep[]
  overallStatus: 'running' | 'done' | 'failed'
  rateLimited: boolean
  onClose: () => void
  onRetry?: () => void
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: CSSProperties = {
  background: 'white',
  borderRadius: '8px',
  padding: '24px',
  minWidth: '420px',
  maxWidth: '600px',
  maxHeight: '80vh',
  overflowY: 'auto',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
}

const stepRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '6px 0',
  fontSize: '14px',
}

const statusIcon = (status: ImportStepStatus) => {
  switch (status) {
    case 'pending': return '⚪'
    case 'running': return '🔵'
    case 'done': return '✅'
    case 'failed': return '❌'
  }
}

export const ImportProgressModal = ({
  open,
  title,
  steps,
  overallStatus,
  rateLimited,
  onClose,
  onRetry,
}: ImportProgressModalProps) => {
  if (!open) return null

  const completed = steps.filter(s => s.status === 'done').length
  const total = steps.length

  return (
    <div style={overlayStyle} role='dialog' aria-modal='true'>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{`Importing ${title}`}</h3>
          <span style={{ fontSize: '13px', color: '#666' }}>
            {`${completed} / ${total}`}
          </span>
        </div>

        {rateLimited && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px 12px',
              background: '#fff4e5',
              border: '1px solid #ffb74d',
              borderRadius: '4px',
              fontSize: '13px',
            }}
          >
            Rate limit reached. Please try again after a few seconds.
          </div>
        )}

        <div style={{ marginTop: '16px' }}>
          {steps.length === 0 ? (
            <div style={{ color: '#666', fontSize: '14px' }}>Preparing…</div>
          ) : (
            steps.map((step, i) => (
              <div key={i} style={stepRowStyle}>
                <span aria-hidden>{statusIcon(step.status)}</span>
                <div style={{ flex: 1 }}>
                  <div>{step.label}</div>
                  {step.error && (
                    <div style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>
                      {step.error}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ marginTop: '20px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {overallStatus === 'failed' && onRetry && (
            <button className='admin-button' type='button' onClick={onRetry}>
              Retry
            </button>
          )}
          <button
            className='admin-button'
            type='button'
            onClick={onClose}
            disabled={overallStatus === 'running'}
          >
            {overallStatus === 'running' ? 'Running…' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}
